import React, { useRef } from 'react'
import { motion } from 'framer-motion'
import { useIntersectionObserver } from '@hooks/useIntersectionObserver'

const About: React.FC = () => {
    const sectionRef = useRef<HTMLElement>(null)
    const entry = useIntersectionObserver(sectionRef, { threshold: 0.1 })
    const isVisible = !!entry?.isIntersecting

    const features = [
        {
            icon: '🏆',
            title: 'Profesionales Certificados',
            description: 'Especialistas con años de experiencia y formación continua en las mejores instituciones.'
        },
        {
            icon: '👩‍⚕️',
            title: 'Experiencia Comprobada',
            description: 'Técnicas tradicionales perfeccionadas con años de práctica para resultados confiables y efectivos.'
        },
        {
            icon: '❤️',
            title: 'Atención Personalizada',
            description: 'Cada paciente recibe un trato único y especializado según sus necesidades específicas.'
        }
    ]

    return (
        <section
            ref={sectionRef}
            id="nosotros"
            className="section-padding bg-white"
        >
            <div className="container-fluid">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    {/* Content */}
                    <motion.div
                        className="space-y-8"
                        initial={{ opacity: 0, x: -50 }}
                        animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div>
                            <h2 className="section-title text-left mb-6">
                                Más de 15 Años
                                <span className="block text-gradient">Cuidando tu Salud</span>
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                En Grupo Médico Empresarial combinamos la experiencia de años en el sector salud
                                con técnicas tradicionales para brindarte servicios de óptica y odontología
                                de la más alta calidad en Toluca, México.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {features.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    className="flex items-start space-x-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                                    transition={{ duration: 0.6, delay: index * 0.2 }}
                                >
                                    <div className="flex-shrink-0 text-3xl">
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-gray-600">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Image */}
                    <motion.div
                        className="relative"
                        initial={{ opacity: 0, x: 50 }}
                        animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                            <img
                                src="/fotoOptica.jpg"
                                alt="Grupo Médico Empresarial - Equipo profesional"
                                className="w-full h-auto object-cover"
                                loading="lazy"
                            />
                            {/* Overlay gradient */}
                            <div className="absolute inset-0 bg-gradient-to-t from-primary-900/20 to-transparent"></div>
                        </div>

                        {/* Decorative elements with 3D effect - MUCHO MÁS VISIBLES */}
                        <motion.div
                            className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-60 blur-2xl"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(147, 51, 234, 0.8))',
                            }}
                            animate={{
                                scale: [1, 1.4, 1],
                                rotate: [0, 180, 360],
                                x: [0, 20, 0],
                                y: [0, 20, 0],
                            }}
                            transition={{
                                duration: 8,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        ></motion.div>

                        <motion.div
                            className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full opacity-50 blur-2xl"
                            style={{
                                background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.8), rgba(249, 115, 22, 0.8))',
                            }}
                            animate={{
                                scale: [1, 1.5, 1],
                                rotate: [360, 180, 0],
                                x: [0, -20, 0],
                                y: [0, -20, 0],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Infinity,
                                ease: "easeInOut"
                            }}
                        ></motion.div>

                        {/* Partículas flotantes adicionales */}
                        {[...Array(6)].map((_, i) => (
                            <motion.div
                                key={`particle-${i}`}
                                className="absolute rounded-full"
                                style={{
                                    width: Math.random() * 60 + 30,
                                    height: Math.random() * 60 + 30,
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                    background: i % 3 === 0
                                        ? 'radial-gradient(circle, rgba(59, 130, 246, 0.6), transparent)'
                                        : i % 3 === 1
                                            ? 'radial-gradient(circle, rgba(236, 72, 153, 0.6), transparent)'
                                            : 'radial-gradient(circle, rgba(6, 182, 212, 0.6), transparent)',
                                    filter: 'blur(10px)',
                                }}
                                animate={{
                                    y: [0, Math.random() * 50 - 25, 0],
                                    x: [0, Math.random() * 50 - 25, 0],
                                    scale: [1, 1.5, 1],
                                    opacity: [0.3, 0.7, 0.3],
                                }}
                                transition={{
                                    duration: Math.random() * 6 + 4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: i * 0.5
                                }}
                            />
                        ))}
                    </motion.div>
                </div>
            </div>
        </section>
    )
}

export default About